"use strict";
import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef
} from "react";
import { Label } from "../Label";
import { Stack } from "../Stack/Stack";
import { randomString } from "../../../utils";
import classNames from "classnames";
import { lightTheme } from "../../../tokens/tokens-css";
import * as styles from "./RadioGroup-css";
const RadioGroupContext = createContext({});
export function RadioGroup({
  value,
  onChange,
  children,
  label,
  labelHidden
}) {
  const labelId = useRef(`radio-group-${randomString()}`);
  const items = useRef([]);
  const registerItem = useCallback((element, value2) => {
    items.current = [...items.current, { element, value: value2 }];
  }, []);
  const unregisterItem = useCallback((element) => {
    items.current = items.current.filter((item) => item.element.id !== element.id);
  }, []);
  const handleKeyDown = (event) => {
    const availableItems = items.current.filter((item) => !item.element.disabled);
    const currentItem = availableItems.find((item) => item.value === value);
    if (!currentItem)
      return;
    const currentIndex = availableItems.indexOf(currentItem);
    const length = availableItems.length;
    const prevIndex = (currentIndex - 1 + length) % length;
    const nextIndex = (currentIndex + 1) % length;
    const prev = availableItems[prevIndex];
    const next = availableItems[nextIndex];
    switch (event.key) {
      case "ArrowUp":
      case "ArrowLeft":
        onChange(prev.value);
        prev.element.focus();
        break;
      case "ArrowDown":
      case "ArrowRight":
        onChange(next.value);
        next.element.focus();
        break;
    }
  };
  return /* @__PURE__ */ React.createElement(RadioGroupContext.Provider, { value: { value, onChange, registerItem, unregisterItem } }, /* @__PURE__ */ React.createElement(Stack, { gap: "m", role: "radiogroup", "aria-labelledby": labelId.current, onKeyDown: handleKeyDown, tabIndex: 0 }, /* @__PURE__ */ React.createElement(Label, { long: true, hidden: labelHidden, id: labelId.current }, label), /* @__PURE__ */ React.createElement(Stack, { gap: "s" }, children)));
}
export function RadioGroupItem({
  label,
  labelHidden,
  value,
  disabled,
  truncate
}) {
  const id = useRef(`radio-${randomString()}`);
  const labelId = useRef(`${id.current}-label`);
  const {
    value: currentValue,
    onChange,
    registerItem,
    unregisterItem
  } = useContext(RadioGroupContext);
  const checked = value === currentValue;
  const buttonRef = useRef(null);
  useLayoutEffect(() => {
    const button = buttonRef.current;
    if (!button)
      return;
    registerItem(button, value);
    return () => {
      unregisterItem(button);
    };
  }, [registerItem, unregisterItem, value]);
  const handleClick = () => {
    onChange(value);
  };
  return /* @__PURE__ */ React.createElement("div", { className: styles.item }, /* @__PURE__ */ React.createElement(
    "button",
    {
      className: styles.radio,
      id: id.current,
      role: "radio",
      type: "button",
      "aria-checked": checked,
      "aria-labelledby": labelId.current,
      onClick: handleClick,
      disabled,
      tabIndex: checked ? 0 : -1,
      value,
      ref: buttonRef
    },
    /* @__PURE__ */ React.createElement("div", { className: classNames(styles.indicator, lightTheme) })
  ), /* @__PURE__ */ React.createElement(
    Label,
    {
      long: true,
      id: labelId.current,
      hidden: labelHidden,
      htmlFor: id.current,
      className: styles.label,
      disabled,
      truncate,
      element: "label",
      align: "left"
    },
    label
  ));
}
